/**
 * Retorna o valor de background CSS baseado no layoutConfig.
 * Se type === "default" ou layoutConfig nulo, retorna null (usa padrão do template).
 */
export function getBackground(layoutConfig) {
  if (!layoutConfig?.background || layoutConfig.background.type === "default") return null;

  if (layoutConfig.background.type === "solid") {
    const { color = "#ffffff", opacity = 100 } = layoutConfig.background.solid ?? {};
    if (opacity >= 100) return color;
    const hex = color.replace("#", "");
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${(opacity / 100).toFixed(2)})`;
  }

  if (layoutConfig.background.type === "gradient") {
    const { direction = "to bottom", stops = [] } = layoutConfig.background.gradient ?? {};
    if (stops.length < 2) return null;
    const stopsStr = stops.map((s) => `${s.color} ${s.position}%`).join(", ");
    return `linear-gradient(${direction}, ${stopsStr})`;
  }

  if (layoutConfig.background.type === "image") {
    const url = layoutConfig.background.image?.url;
    if (!url) return null;
    return `url(${JSON.stringify(url)}) center center / cover no-repeat`;
  }

  return null;
}

const ADDRESS_DISPLAY_MODES = new Set(["none", "text", "pin", "pinText"]);
const ADDRESS_ALIGNMENTS = new Set(["left", "center", "right", "justify"]);
const ADDRESS_PIN_BORDER_MODES = new Set(["default", "none", "custom"]);
const ADDRESS_SIDE_POSITIONS = new Set(["top", "bottom", "left", "right"]);
const ADDRESS_PLACE_REFERENCES = new Set(["pin", "text", "block"]);
const COVER_IMAGE_MODES = new Set(["default", "event", "custom", "none"]);
const BANNER_MODES = new Set(["default", "custom", "none"]);
export const RSVP_FONT_OPTIONS = [
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Trebuchet MS', sans-serif", label: "Trebuchet" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "'Courier New', monospace", label: "Courier New" },
  { value: "'Brush Script MT', cursive", label: "Cursiva" },
];
const RSVP_FONT_FAMILY_SET = new Set(RSVP_FONT_OPTIONS.map((option) => option.value));

export function getAddressDisplayMode(layoutConfig, fallback = "pinText") {
  const value = layoutConfig?.addrDisplayMode;
  return ADDRESS_DISPLAY_MODES.has(value) ? value : fallback;
}

export function getAddressAlignment(layoutConfig, fallback = "center") {
  const value = layoutConfig?.addrAlign;
  return ADDRESS_ALIGNMENTS.has(value) ? value : fallback;
}

export function getTextAlignValue(alignment) {
  return alignment === "justify" ? "justify" : alignment;
}

export function getFlexJustifyValue(alignment, multipleItems = true) {
  if (alignment === "center") return "center";
  if (alignment === "right") return "flex-end";
  if (alignment === "justify") return multipleItems ? "space-between" : "center";
  return "flex-start";
}

export function getAddressPinBorderMode(layoutConfig, fallback = "default") {
  const value = layoutConfig?.addrPinBorderMode;
  return ADDRESS_PIN_BORDER_MODES.has(value) ? value : fallback;
}

export function getAddressTextPosition(layoutConfig, fallback = "right") {
  const value = layoutConfig?.addrTextPosition;
  return ADDRESS_SIDE_POSITIONS.has(value) ? value : fallback;
}

export function getAddressPlaceVisible(layoutConfig) {
  return layoutConfig?.addrShowPlace !== false;
}

export function getAddressPlacePosition(layoutConfig, fallback = "bottom") {
  const value = layoutConfig?.addrPlacePosition;
  return ADDRESS_SIDE_POSITIONS.has(value) ? value : fallback;
}

export function getAddressPlaceReference(layoutConfig, fallback = "pin") {
  const value = layoutConfig?.addrPlaceReference;
  return ADDRESS_PLACE_REFERENCES.has(value) ? value : fallback;
}

export function hasAddressContent(layoutConfig, nomeEspaco, fallbackMode = "pinText") {
  const mode = getAddressDisplayMode(layoutConfig, fallbackMode);
  return mode !== "none" || (getAddressPlaceVisible(layoutConfig) && !!nomeEspaco);
}

export function sanitizeRsvpFontFamily(value) {
  return RSVP_FONT_FAMILY_SET.has(value) ? value : null;
}

export function getCoverImageMode(layoutConfig, fallback = "none") {
  const value = layoutConfig?.coverImageMode;
  if (value === "event") return "default";
  return COVER_IMAGE_MODES.has(value) ? value : fallback;
}

export function getResolvedCoverImageUrl(layoutConfig, themeImageUrl = null) {
  const mode = getCoverImageMode(layoutConfig);
  if (mode === "none") return null;
  if (mode === "custom") return layoutConfig?.coverImageUrl || null;
  return themeImageUrl || null;
}

export function getBannerMode(layoutConfig, fallback = "default") {
  const value = layoutConfig?.bannerMode;
  if (BANNER_MODES.has(value)) return value;
  return layoutConfig?.bannerUrl ? "custom" : fallback;
}

const CIRCLE_SIZES = { P: 90, M: 140, G: 200, GG: 225 };
const SQUARE_SIZES = { P: 140, M: 220, G: 320, GG: 360 };

/**
 * Retorna os estilos inline para o elemento <img> da imagem de capa.
 * Retorna null se layoutConfig não definir coverImage.
 */
export function getCoverImageStyle(layoutConfig) {
  if (!layoutConfig?.coverImage) return null;
  const { shape = "circle", size = "M" } = layoutConfig.coverImage;
  const dim = shape === "circle" ? CIRCLE_SIZES[size] ?? CIRCLE_SIZES.M : null;
  const squareDim = SQUARE_SIZES[size] ?? SQUARE_SIZES.M;

  if (shape === "circle") {
    return { width: dim, height: dim, borderRadius: "50%", objectFit: "cover", display: "block" };
  } else {
    return { width: squareDim, maxWidth: "100%", height: "auto", borderRadius: 12, objectFit: "cover", display: "block" };
  }
}

/**
 * Retorna os estilos para o wrapper da imagem de capa.
 * Para círculo: div centralizado com dimensões fixas.
 * Para quadrado: div centralizado com maxWidth.
 */
export function getCoverImageWrapperStyle(layoutConfig) {
  if (!layoutConfig?.coverImage) return null;
  const { shape = "circle", size = "M" } = layoutConfig.coverImage;
  const dim = shape === "circle" ? CIRCLE_SIZES[size] ?? CIRCLE_SIZES.M : null;
  const squareDim = SQUARE_SIZES[size] ?? SQUARE_SIZES.M;

  if (shape === "circle") {
    return { width: dim, height: dim, borderRadius: "50%", overflow: "hidden", margin: "0 auto" };
  } else {
    return { width: squareDim, maxWidth: "100%", borderRadius: 12, overflow: "hidden", margin: "0 auto" };
  }
}

export const defaultLayoutConfig = {
  coverImage: { shape: "circle", size: "M" },
  coverImageMode: "none",
  coverImageUrl: null,
  background: {
    type: "default",
    solid: { color: "#ffffff", opacity: 100 },
    gradient: {
      direction: "to bottom",
      stops: [
        { color: "#ffffff", position: 0 },
        { color: "#cccccc", position: 100 },
      ],
    },
    image: { url: null },
  },
  sections: { howToGet: true, address: true },
  bannerMode: "default",
  bannerUrl: null,
  headerBg: null,
  headerBgImageUrl: null,
  headerTitleVisible: null,
  headerTitleColor: null,
  headerTitleShadowMode: null,
  headerTitleShadowColor: null,
  headerTitleShadowOffsetX: null,
  headerTitleShadowOffsetY: null,
  headerTitleShadowBlur: null,
  headerDescBg: null,
  headerDescColor: null,
  badgeImageMode: null,
  badgeImageUrl: null,
  badgeImageAlign: null,
  badgeImageSize: null,
  badgeImagePlacement: null,
  dateBg: null,
  dateTextColor: null,
  dateDayColor: null,
  dateTimeColor: null,
  dateDividerColor: null,
  addrBg: null,
  addrTextColor: null,
  addrDividerColor: null,
  addrDisplayMode: null,
  addrAlign: null,
  addrTextPosition: null,
  addrShowPlace: null,
  addrShowWaze: null,
  addrPlacePosition: null,
  addrPlaceReference: null,
  addrPinBg: null,
  addrPinBorderMode: null,
  addrPinBorderColor: null,
  rsvpBg: null,
  rsvpTextColor: null,
  rsvpSectionBg: null,
  rsvpSectionTitleColor: null,
  rsvpPeopleBg: null,
  rsvpPeopleTextColor: null,
  rsvpPeopleTitleColor: null,
  rsvpPeopleFontFamily: null,
  rsvpPeopleInputBg: null,
  rsvpPeopleInputTextColor: null,
  rsvpPeopleInputPlaceholderColor: null,
  rsvpMessageBg: null,
  rsvpMessageTextColor: null,
  rsvpMessageTitleColor: null,
  rsvpMessageFontFamily: null,
  rsvpMessageInputBg: null,
  rsvpMessageInputTextColor: null,
  rsvpMessageInputPlaceholderColor: null,
  rsvpClosedBg: null,
  rsvpClosedTitleColor: null,
  rsvpClosedTitleWeight: null,
  rsvpClosedTextColor: null,
  rsvpAddButtonBg: null,
  rsvpAddButtonTextColor: null,
  rsvpSubmitButtonBg: null,
  rsvpSubmitButtonTextColor: null,
  rsvpInputBg: null,
  rsvpInputTextColor: null,
  rsvpInputPlaceholderColor: null,
  topDecoText: null,
  topDecoVisible: null,
  topDecoAlign: null,
  topDecoColor: null,
  bottomDecoText: null,
  bottomDecoVisible: null,
  bottomDecoAlign: null,
  bottomDecoColor: null,
};
